import { Reveal } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Checkbox, RadioGroup, TextBox } from '@progress/kendo-react-inputs';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiEnvironments, LoginResultCode } from '../../../common/enums';
import { canChangeEnvironment, envConfig, getRemoteAuthenticationUrl, selectEnvironment } from '../../../configs/env';
import { ChevronDownOutline, ChevronUpOutline, ExternalLinkOutline } from '../../../icons/icons';
import { LoadingIndicatorCircular } from '../../components/indicators/LoadingIndicatorCircular';
import { useAuthProvider } from '../../contexts/AuthProvider';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { ROUTES } from '../../router/routes';
import { setSetting } from '../../state/slices/settings-slice';
import { RadioButtonList, useFormStringValue } from '../home/hooks/forms';
import { useSettingCheckboxValue } from '../home/hooks/settings';

export default function Login() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [busy, setBusy] = React.useState(false);
    const [errorText, setErrorText] = React.useState<string>();
    const [env, setEnv] = React.useState(envConfig.type);
    const username = useFormStringValue('');
    const password = useFormStringValue('');
    const rememberMe = useSettingCheckboxValue('rememberMe');
    const rememberIntegrationId = useSettingCheckboxValue('rememberIntegrationId');
    const auth = useAuthProvider();
    const integrationExpanded = useSettingCheckboxValue('integrationSectionUnfolded');
    const dispatch = useAppDispatch();
    const storedIntegrationId = useAppSelector((state) => state.settings.integrationId);
    const integrationId = useFormStringValue(rememberIntegrationId && storedIntegrationId ? storedIntegrationId : '');

    const handleLogin = async () => {
        setBusy(true);
        setErrorText(undefined);

        const result = await auth.instance.loginWithCredentials(username.value, password.value);
        if (result.ok) {
            if (integrationId.value && integrationId.value.trim().length > 0) {
                dispatch(setSetting('integrationId', integrationId.value.trim()));
            }
            navigate(searchParams.get('returnUrl') || ROUTES.HOME, { replace: true });
        } else {
            setErrorText(errorMessages[result.code]);
        }
        setBusy(false);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    return (
        <div>
            <div className="p-2 d-flex gap-2 flex-column gap-3">
                <div>
                    <div className="pb-1">User ID</div>
                    <TextBox {...username} disabled={busy} placeholder="Login" name="username" onKeyDown={handleKeyDown} />
                </div>
                <div>
                    <div className="pb-1">Password</div>
                    <TextBox
                        {...password}
                        disabled={busy}
                        placeholder="Password"
                        type="password"
                        name="password"
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <Checkbox {...rememberMe} disabled={busy} label="Remember me" />

                <div>
                    <div
                        className=" d-flex gap-2 align-items-center cursor-pointer"
                        onClick={() => integrationExpanded.onChange({ value: !integrationExpanded.value } as any)}>
                        <SvgIcon icon={integrationExpanded.value ? ChevronUpOutline : ChevronDownOutline} />
                        <div className="secondary-text">Integration ID (Optional)</div>
                    </div>
                    <Reveal className="w-100">
                        {integrationExpanded.value && (
                            <div className="p-2 d-flex flex-column gap-2">
                                <TextBox {...integrationId} disabled={busy} />
                                <Checkbox {...rememberIntegrationId} disabled={busy} label="Remember integration ID" />
                            </div>
                        )}
                    </Reveal>
                </div>

                {busy && <LoadingIndicatorCircular className="align-self-center" />}
                {errorText && <div className="text-danger">{errorText}</div>}
                <div className="d-flex flex-column gap-2">
                    <Button disabled={busy} themeColor={'primary'} onClick={handleLogin}>
                        Login
                    </Button>

                    <Button
                        disabled={busy}
                        fillMode={'outline'}
                        themeColor={'primary'}
                        svgIcon={ExternalLinkOutline}
                        onClick={() => (window.location.href = getRemoteAuthenticationUrl())}>
                        Login with CAE Community Portal
                    </Button>
                </div>

                {canChangeEnvironment && (
                    <>
                        <div className="text-center">Api environment:</div>
                        <RadioGroup
                            className="justify-content-center"
                            disabled={busy}
                            value={env}
                            onChange={(e) => {
                                setEnv(e.value);
                                selectEnvironment(e.value);
                                dispatch(setSetting('overrideApiEnvironment', e.value));
                            }}
                            data={apiEnvs}
                            layout="horizontal"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

const apiEnvs: RadioButtonList<ApiEnvironments> = [
    { value: ApiEnvironments.local, label: 'Local' },
    { value: ApiEnvironments.dev, label: 'Dev' },
    { value: ApiEnvironments.dev2, label: 'Dev2' },
    { value: ApiEnvironments.cert, label: 'Cert' },
    { value: ApiEnvironments.prod, label: 'Prod (ORE)' },
    { value: ApiEnvironments.prodAlt, label: 'Prod (IRE)' }
];

const errorMessages: Record<LoginResultCode, string> = {
    [LoginResultCode.ok]: '',
    [LoginResultCode.notFound]: 'ID and/or password incorrect.',
    [LoginResultCode.noAccess]: 'Account not authorized for web access',
    [LoginResultCode.expired]: 'Account expired. Please contact Flight Explorer support.',
    [LoginResultCode.disabled]: 'Account disabled. Please contact Flight Explorer support.',
    [LoginResultCode.tooManySessions]: 'Connection limit exceeded.',
    [LoginResultCode.serviceUnavailable]: 'Service unavailable. Please try again later'
};
